// Identity contracts — users, branch assignments, the session view (modules-and-api.md §5.2, §5.4; TEN-24, TEN-27..33, TEN-38..47; R-03, R-05, R-21).
// No schema here ever carries a password, PIN, TOTP secret or hash (BR-IDENTITY-026).
import { z } from "zod";
import { DEVICE_KINDS, UserSummarySchema } from "./auth";
import { PERMISSION_CODES, ROLES } from "./permissions";
import { EmailSchema, InstantSchema, NonNegativeCentsSchema, UlidSchema } from "./primitives";
import { SessionHandoverSchema } from "./patients";
import { COMMERCIAL_MODELS, FeatureFlagsViewSchema, ORGANISATION_STATUSES } from "./tenancy";

/** Roles a tenant may assign. `FranchisorAdmin` only in a Franchisor organisation (R-21); `PlatformAdmin` never (TEN-29). */
export const ASSIGNABLE_ROLES = [...ROLES, "FranchisorAdmin"] as const;
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];
export const USER_STATUSES = ["Invited", "Active", "Deactivated"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** R-05: displayed zero-padded to four digits. */
export const controlIdDisplay = (controlId: number): string => String(controlId).padStart(4, "0");

const hpcsa = z.string().trim().min(3).max(20);
const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();

/** HPCSA is mandatory for an Optometrist or any clinician (R-21, BR-IDENTITY-020). */
export function hpcsaRequired(v: { role: string; isClinician?: boolean | undefined }): boolean {
  return v.role === "Optometrist" || v.isClinician === true;
}

export const InviteUserInputSchema = z.strictObject({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  initials: z.string().trim().max(10).optional(),
  email: EmailSchema,
  phone: z.string().trim().max(40).optional(),
  role: z.enum(ASSIGNABLE_ROLES),
  isClinician: z.boolean().optional(),
  branchIds: z.array(UlidSchema).min(1).max(50),
  homeBranchId: UlidSchema,
  branchOwnerBranchIds: z.array(UlidSchema).max(50).optional(),
  hpcsaNumber: hpcsa.optional(),
  licenseNumber: z.string().trim().max(40).optional(),
  practiceNumber: z.string().trim().max(20).optional(),
  isSalesPerson: z.boolean().optional(),
  salesTargetCents: NonNegativeCentsSchema.optional(),
}).superRefine((v, ctx) => {
  if (hpcsaRequired(v) && !v.hpcsaNumber) ctx.addIssue({ code: "custom", path: ["hpcsaNumber"], message: "HPCSA number is required for an Optometrist or clinician", params: { code: "required" } });
  if (!v.branchIds.includes(v.homeBranchId)) ctx.addIssue({ code: "custom", path: ["homeBranchId"], message: "the home branch must be one of the assigned branches", params: { code: "not_assigned" } });
  if (new Set(v.branchIds).size !== v.branchIds.length) ctx.addIssue({ code: "custom", path: ["branchIds"], message: "a branch appears more than once", params: { code: "duplicate" } });
  for (const b of v.branchOwnerBranchIds ?? []) if (!v.branchIds.includes(b)) ctx.addIssue({ code: "custom", path: ["branchOwnerBranchIds"], message: "Branch Owner designation requires an assignment to that branch", params: { code: "not_assigned" } });
});
export type InviteUserInput = z.infer<typeof InviteUserInputSchema>;

export const InviteUserResponseSchema = z.object({
  userId: UlidSchema,
  controlId: z.number().int(),
  controlIdDisplay: z.string(),
  invitationExpiresAt: InstantSchema,
  /** Present only outside production so tests and staging bootstrap can accept the invitation without an inbox. */
  invitationToken: z.string().optional(),
});

/** PATCH /users/{id}. Role, clinician flag and licence fields need `identity.user.write`; a user may edit only their own details (R-03). */
export const UserPatchSchema = z.strictObject({
  firstName: z.string().trim().min(1).max(80).optional(),
  lastName: z.string().trim().min(1).max(80).optional(),
  initials: z.string().trim().max(10).nullable().optional(),
  phone: optionalText(40),
  role: z.enum(ASSIGNABLE_ROLES).optional(),
  isClinician: z.boolean().optional(),
  hpcsaNumber: hpcsa.nullable().optional(),
  licenseNumber: optionalText(40),
  practiceNumber: optionalText(20),
  isSalesPerson: z.boolean().optional(),
});
export type UserPatch = z.infer<typeof UserPatchSchema>;
/** The fields a user may change on their own record without `identity.user.write` (UC-IDENTITY-012 A2). */
export const SELF_EDITABLE_USER_FIELDS: ReadonlySet<keyof UserPatch> = new Set<keyof UserPatch>(["firstName", "lastName", "initials", "phone"]);

export const UserViewSchema = UserSummarySchema.extend({
  initials: z.string().nullable(),
  phone: z.string().nullable(),
  status: z.enum(USER_STATUSES),
  isActive: z.boolean(),
  hpcsaNumber: z.string().nullable(),
  licenseNumber: z.string().nullable(),
  practiceNumber: z.string().nullable(),
  isSalesPerson: z.boolean(),
  pinSet: z.boolean(),
  pinMustChange: z.boolean(),
  pinLockedUntil: InstantSchema.nullable(),
  loginLockedUntil: InstantSchema.nullable(),
  mustChangePassword: z.boolean(),
  lastLoginAt: InstantSchema.nullable(),
  deactivatedAt: InstantSchema.nullable(),
  deactivatedBy: UlidSchema.nullable(),
  deactivationReason: z.string().nullable(),
  version: z.number().int(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type UserView = z.infer<typeof UserViewSchema>;

export const UserLifecycleRequestSchema = z.strictObject({ reason: z.string().trim().min(1).max(500).optional() });
export const UserLifecycleResponseSchema = UserViewSchema.extend({ changed: z.boolean() });

export const AssignmentInputSchema = z.strictObject({
  branchId: UlidSchema,
  isHomeBranch: z.boolean().optional(),
  isBranchOwner: z.boolean().optional(),
});
export const AssignmentsPutSchema = z.strictObject({ assignments: z.array(AssignmentInputSchema).min(1).max(50) }).superRefine((v, ctx) => {
  const homes = v.assignments.filter((a) => a.isHomeBranch).length;
  if (homes !== 1) ctx.addIssue({ code: "custom", path: ["homeBranchId"], message: "exactly one assignment must be the home branch", params: { code: homes === 0 ? "required" : "duplicate" } });
  const ids = v.assignments.map((a) => a.branchId);
  if (new Set(ids).size !== ids.length) ctx.addIssue({ code: "custom", path: ["branchIds"], message: "a branch appears more than once", params: { code: "duplicate" } });
});
export type AssignmentsPut = z.infer<typeof AssignmentsPutSchema>;

export const AssignmentViewSchema = z.object({
  branchId: UlidSchema,
  branchCode: z.string(),
  branchName: z.string(),
  branchStatus: z.enum(["Active", "Inactive"]),
  isHomeBranch: z.boolean(),
  isBranchOwner: z.boolean(),
  assignedAt: InstantSchema,
});
export type AssignmentView = z.infer<typeof AssignmentViewSchema>;
export const AssignmentsViewSchema = z.object({
  userId: UlidSchema,
  assignments: z.array(AssignmentViewSchema),
  warnings: z.array(z.object({ path: z.string(), code: z.string(), message: z.string(), branchId: UlidSchema.optional() })),
});

export const ResetPinResponseSchema = z.object({
  /** Shown once to the administrator for in-person handover; never emailed (R-06). */
  oneTimePin: z.string(),
  pinMustChange: z.literal(true),
});
export const ResetPinRequestSchema = z.strictObject({ reason: z.string().trim().max(500).optional() });
export const ForcePasswordResetResponseSchema = z.object({ resetExpiresAt: InstantSchema, sessionsRevoked: z.number().int() });
export const ResendInvitationResponseSchema = z.object({ invitationExpiresAt: InstantSchema, invitationToken: z.string().optional() });
export const ClearLockResponseSchema = z.object({ loginLockCleared: z.boolean(), pinLockCleared: z.boolean() });

// --- The acting session (GET /auth/session) --------------------------------------------------------------------------------

export const SessionBranchSchema = z.object({ id: UlidSchema, code: z.string(), name: z.string(), status: z.enum(["Active", "Inactive"]) });

export const SessionViewSchema = z.object({
  sessionId: UlidSchema,
  user: UserSummarySchema,
  /** `status` is `Suspended` for the read-only session of a suspended organisation's Owner (UC-IDENTITY-001 A5, D28). */
  organisation: z.object({ id: UlidSchema, legalName: z.string(), tradingName: z.string().nullable(), commercialModel: z.enum(COMMERCIAL_MODELS), status: z.enum(ORGANISATION_STATUSES) }),
  assignments: z.array(AssignmentViewSchema),
  activeBranchId: UlidSchema.nullable(),
  /** Branches the user may switch to: every Active branch for Owner and Admin, the Active assigned ones otherwise (TEN-26). */
  switchableBranches: z.array(SessionBranchSchema),
  canSwitchBranch: z.boolean(),
  permissions: z.array(z.enum(PERMISSION_CODES as [string, ...string[]])),
  /** Codes held only within named branches — a Branch Owner's designation grants (D39, D40). A code absent here is held for the whole organisation. */
  branchScopedPermissions: z.array(z.object({ code: z.enum(PERMISSION_CODES as [string, ...string[]]), branchIds: z.array(UlidSchema) })),
  featureFlags: FeatureFlagsViewSchema,
  settings: z.object({
    locale: z.string(), currency: z.string(), timezone: z.string(), vatRateBp: z.number().int(),
    pinMinLength: z.number().int(), pinMaxLength: z.number().int(),
    sessionIdleMinutesDesktop: z.number().int(), sessionIdleMinutesTablet: z.number().int(), sessionAbsoluteHours: z.number().int(),
    editLockIdleMinutes: z.number().int(), handoverIdleMinutes: z.number().int(),
    discountPinThresholdPercentBp: z.number().int(), discountPinThresholdCents: z.number().int(),
    patientSharingMode: z.enum(["Tenant", "Branch"]),
  }),
  pinSetupRequired: z.boolean(),
  deviceKind: z.enum(DEVICE_KINDS).nullable(),
  isLocked: z.boolean(),
  expiresAt: InstantSchema,
  idleExpiresAt: InstantSchema,
  /** Present while the session is in kiosk mode (TEN-65, UC-PATIENTS-022 step 2): a reloaded kiosk learns from here that it is handed over, for what and until when. */
  handover: SessionHandoverSchema.optional(),
});
export type SessionView = z.infer<typeof SessionViewSchema>;
